import { useEffect, type ReactNode } from 'react';

/**
 * 모달 껍데기.
 *
 * <p><b>버튼은 제목 줄에 둔다.</b> 좁아서 깨지더라도 자리를 옮기지 않는다 — 화면마다 자리가
 * 다르면 어디를 눌러야 할지 매번 다시 찾게 된다(front 의 `Modal` 과 같은 규율).
 *
 * <p>모바일에서는 아래에서 올라오는 시트, 넓은 화면에서는 가운데 카드. 높이를 제한하고 본문만
 * 스크롤한다 — 제목 줄의 버튼이 목록을 따라 화면 밖으로 밀려나지 않는다.
 *
 * <p>배경을 눌러도 닫히지 않는다. 입력 중인 판이 대부분이라, 여백을 무심히 누른 것만으로 적던
 * 내용이 사라지면 되돌릴 방법이 없다. 닫기는 제목 줄의 버튼과 Esc 로만 한다.
 */
export function Modal({
  title,
  actions,
  onClose,
  children,
  maxWidth = '28rem',
}: {
  title: string;
  /** 제목 줄 오른쪽 끝의 버튼들. 닫기·저장이 여기 선다. */
  actions?: ReactNode;
  onClose: () => void;
  children: ReactNode;
  maxWidth?: string;
}) {
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center" role="dialog" aria-modal="true" aria-label={title}>
      <div className="absolute inset-0" style={{ background: 'rgba(15, 23, 42, 0.4)' }} aria-hidden />

      <div
        className="relative flex max-h-[85vh] w-full flex-col rounded-t-xl sm:rounded-xl"
        style={{ background: 'var(--bg-card)', maxWidth }}
      >
        <div
          className="flex shrink-0 items-center justify-between gap-2 border-b px-4 py-3"
          style={{ borderColor: 'var(--border)' }}
        >
          <h2 className="min-w-0 truncate text-ui-lg font-semibold">{title}</h2>
          {/* 줄이 모자라면 옆으로 민다. 접어서 두 줄로 만들지 않는다 — 머리 줄 높이가 창마다 달라진다. */}
          <div className="flex shrink-0 items-center gap-2 overflow-x-auto">{actions}</div>
        </div>

        <div className="flex-1 overflow-y-auto p-4">{children}</div>
      </div>
    </div>
  );
}
